/**
 * Dual Scroll Product Story
 * Scroll-driven sticky scrollytelling custom element
 *
 * TRANSITION ARCHITECTURE (v2 — clean segment-based crossfade):
 *
 *   maxIndex    = slideCount - 1
 *   slideFloat  = progress × maxIndex
 *
 *   progress ≤ 0 → show slide 0 fully, hide all others
 *   progress ≥ 1 → show last slide fully, hide all others
 *
 *   Otherwise:
 *     baseIndex    = floor(slideFloat)
 *     nextIndex    = min(baseIndex + 1, maxIndex)
 *     localProgress = slideFloat - baseIndex  (0..1 within segment)
 *     t = smoothstep(0.15, 0.85, localProgress)
 *
 *   Base slide:  opacity = 1 - t,  textY = ease(t)×offset,  prodY = -ease(t)×offset
 *   Next slide:  opacity = t,      textY = -offset×(1-ease(t)),  prodY = offset×(1-ease(t))
 *   All others:  opacity = 0, hidden
 *
 *   The base slide starts fully visible and fades out as next fades in.
 *   First slide NEVER starts at opacity 0.
 *   Last slide ALWAYS reaches opacity 1.
 *
 *   Smoothing: targetProgress → currentProgress with exponential ease,
 *   but progress endpoints (0 and 1) are forced to prevent lag.
 */

if (!customElements.get('dual-scroll-product-story')) {
  class DualScrollProductStory extends HTMLElement {
    constructor() {
      super();

      // Config
      this.sectionId = '';
      this.enableStickyDesktop = false;
      this.enableStickyMobile = false;
      this.slideCount = 0;
      this.transitionIntensity = 0.6;
      this.smoothing = 0.12;
      this.textVerticalAlign = 'center';
      this.productVerticalAlign = 'center';

      // Scroll state
      this.stickyStart = 0;
      this.scrollRange = 0;
      this.progress = 0;
      this.activeSlideIndex = 0;
      this.isInStickyRange = false;

      // Animation state — two progress values for smooth interpolation
      this.targetProgress = 0;
      this.currentProgress = 0;

      // DOM caches
      this.pinWrapper = null;
      this.slidesContainer = null;
      this.slides = [];
      this.textPanels = [];
      this.productPanels = [];
      this.dots = null;
      this.progressBar = null;

      // Bound handlers
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._debounce(this._onResize.bind(this), 150);
      this._animationLoop = this._animationLoop.bind(this);
      this._onSectionLoad = this._onSectionLoad.bind(this);
      this._onMotionChange = this._onMotionChange.bind(this);

      this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    /* ─── LIFECYCLE ─── */

    connectedCallback() {
      this._readConfig();
      this._cacheDOM();
      if (this.slides.length === 0) return;

      this._applyVerticalAlignment();
      this._calculateLayout();
      this._createProgressIndicator();
      this._bindEvents();

      // Initialize first slide as fully visible
      this._renderSlides(0);

      // Start render loop
      this._rafId = requestAnimationFrame(this._animationLoop);

      // Shopify theme editor
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', this._onSectionLoad);
      }
    }

    disconnectedCallback() {
      this._unbindEvents();
      cancelAnimationFrame(this._rafId);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (window.Shopify && window.Shopify.designMode) {
        document.removeEventListener('shopify:section:load', this._onSectionLoad);
      }
    }

    /* ─── CONFIG ─── */

    _readConfig() {
      this.sectionId = this.dataset.sectionId || '';
      this.enableStickyDesktop = this.dataset.enableStickyDesktop === 'true';
      this.enableStickyMobile = this.dataset.enableStickyMobile === 'true';
      this.slideCount = parseInt(this.dataset.slideCount, 10) || 0;
      this.transitionIntensity = (parseInt(this.dataset.transitionIntensity, 10) || 60) / 100;
      this.smoothing = parseFloat(this.dataset.smoothing) || 0.12;
      this.textVerticalAlign = this.dataset.textVerticalAlign || 'center';
      this.productVerticalAlign = this.dataset.productVerticalAlign || 'center';
    }

    _cacheDOM() {
      this.pinWrapper = this.querySelector('.dual-scroll-story__pin-wrapper');
      this.slidesContainer = this.querySelector('.dual-scroll-story__slides');
      this.slides = Array.from(this.querySelectorAll('.dual-scroll-story__slide'));
      this.textPanels = Array.from(this.querySelectorAll('.dual-scroll-story__text-panel'));
      this.productPanels = Array.from(this.querySelectorAll('.dual-scroll-story__product-panel'));
    }

    /* ─── VERTICAL ALIGNMENT ─── */

    _applyVerticalAlignment() {
      this.textPanels.forEach(panel => {
        panel.setAttribute('data-align', this.textVerticalAlign);
      });
      this.productPanels.forEach(panel => {
        panel.setAttribute('data-align', this.productVerticalAlign);
      });
    }

    /* ─── LAYOUT CALCULATION ─── */

    _calculateLayout() {
      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      this._isDesktop = isDesktop;

      // Should sticky be active for this breakpoint?
      this._shouldSticky = (isDesktop ? this.enableStickyDesktop : this.enableStickyMobile)
        && !this._reducedMotion.matches
        && this.slideCount > 1;

      if (this._shouldSticky) {
        this._applyStickyHeight();
      } else {
        this._removeStickyHeight();
      }

      // Mobile sticky class
      if (this.enableStickyMobile && !isDesktop && !this._reducedMotion.matches && this.slideCount > 1) {
        this.classList.add('dual-scroll-story--sticky-enabled-mobile');
      } else {
        this.classList.remove('dual-scroll-story--sticky-enabled-mobile');
      }

      // Movement offset: subtle, proportional to viewport and intensity
      const viewportHeight = window.innerHeight;
      // At intensity 60%, offset ≈ 36px on a 1000px viewport — subtle but visible
      this._moveOffset = Math.round(viewportHeight * 0.04 * this.transitionIntensity * 10);

      // Cache sticky positions
      this._calculateStickyPositions();

      // Re-render current state
      if (this._shouldSticky) {
        this._renderSlides(this.currentProgress);
      }
    }

    _applyStickyHeight() {
      const viewportHeight = window.innerHeight;
      const maxIndex = this.slideCount - 1;

      // Each segment gets one viewport height of scroll distance.
      // Add 25% extra hold distance so the last slide has time to fully settle.
      const segmentDistance = viewportHeight;
      const holdDistance = viewportHeight * 0.25;

      this.scrollRange = maxIndex * segmentDistance + holdDistance;
      this._segmentDistance = segmentDistance;

      const totalHeight = viewportHeight + this.scrollRange;
      this.style.height = totalHeight + 'px';
    }

    _removeStickyHeight() {
      this.style.height = '';
      this.scrollRange = 0;
    }

    _calculateStickyPositions() {
      if (!this._shouldSticky) {
        this.stickyStart = 0;
        return;
      }
      this.stickyStart = this.offsetTop;
    }

    /* ─── EVENTS ─── */

    _bindEvents() {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);

      if (window.ResizeObserver && this.pinWrapper) {
        this._resizeObserver = new ResizeObserver(() => {
          this._calculateLayout();
        });
        this._resizeObserver.observe(this.pinWrapper);
      }

      this._reducedMotion.addEventListener('change', this._onMotionChange);
    }

    _unbindEvents() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      this._reducedMotion.removeEventListener('change', this._onMotionChange);
    }

    /* ─── SCROLL HANDLER ─── */

    _onScroll() {
      if (!this._shouldSticky) return;

      const scrollY = window.scrollY;
      const stickyEnd = this.stickyStart + this.scrollRange;

      if (scrollY >= this.stickyStart && scrollY <= stickyEnd) {
        this.isInStickyRange = true;
        this.targetProgress = (scrollY - this.stickyStart) / this.scrollRange;
        this.targetProgress = Math.max(0, Math.min(1, this.targetProgress));
      } else if (scrollY > stickyEnd) {
        // Past the section — force to final state
        this.isInStickyRange = true;
        this.targetProgress = 1;
      } else {
        // Before the section — force to initial state
        this.isInStickyRange = false;
        this.targetProgress = 0;
      }
    }

    /* ─── ANIMATION LOOP ─── */

    _animationLoop() {
      // Smooth interpolation of progress
      const diff = this.targetProgress - this.currentProgress;

      if (Math.abs(diff) < 0.0005) {
        // Close enough — snap to target
        this.currentProgress = this.targetProgress;
      } else if (this.targetProgress === 0 && this.currentProgress < 0.01) {
        // Force to 0 when target is 0 and we're very close
        this.currentProgress = 0;
      } else if (this.targetProgress === 1 && this.currentProgress > 0.99) {
        // Force to 1 when target is 1 and we're very close
        this.currentProgress = 1;
      } else {
        // Exponential ease toward target
        this.currentProgress += diff * this.smoothing;
      }

      this.progress = this.currentProgress;

      if (this._shouldSticky && this.slideCount > 1) {
        this._renderSlides(this.progress);
      }

      this._rafId = requestAnimationFrame(this._animationLoop);
    }

    /* ─── SLIDE RENDERING — Clean Segment-Based Crossfade ─── */

    _renderSlides(progress) {
      const maxIndex = this.slideCount - 1;
      if (maxIndex <= 0) return;

      // Clamp progress
      progress = Math.max(0, Math.min(1, progress));

      let baseIndex, nextIndex, t;

      if (progress <= 0) {
        // Before the section — show first slide fully
        baseIndex = 0;
        nextIndex = 0;
        t = 0;
      } else if (progress >= 1) {
        // After all transitions — show last slide fully
        baseIndex = maxIndex;
        nextIndex = maxIndex;
        t = 1;
      } else {
        // Calculate which segment we're in
        const slideFloat = progress * maxIndex;
        baseIndex = Math.min(Math.floor(slideFloat), maxIndex);
        nextIndex = Math.min(baseIndex + 1, maxIndex);
        const localProgress = slideFloat - baseIndex;

        // Smoothstep: keeps slides mostly visible, transitions happen in the middle
        t = this._smoothstep(0.15, 0.85, localProgress);
      }

      // Track active index for dots
      const dominantIndex = t < 0.5 ? baseIndex : nextIndex;
      if (dominantIndex !== this.activeSlideIndex) {
        this.activeSlideIndex = dominantIndex;
        this._updateProgressIndicator(dominantIndex);
      }

      // Update progress bar
      this._updateProgressBar();

      // Apply transforms to each slide
      const offset = this._moveOffset;

      this.slides.forEach((slide, i) => {
        const textPanel = this.textPanels[i];
        const productPanel = this.productPanels[i];
        if (!textPanel || !productPanel) return;

        if (i === baseIndex && i === nextIndex) {
          // Only one slide visible (progress=0 or progress=1, or single segment)
          // Fully visible, no transform
          slide.style.opacity = 1;
          slide.style.zIndex = 3;
          slide.style.pointerEvents = 'auto';
          textPanel.style.transform = 'translateY(0)';
          textPanel.style.opacity = 1;
          productPanel.style.transform = 'translateY(0)';
          productPanel.style.opacity = 1;
          // Reset product image scale
          const img = slide.querySelector('.dual-scroll-story__product-image');
          if (img) img.style.transform = 'scale(1)';

        } else if (i === baseIndex) {
          // BASE SLIDE — currently visible, fading out
          // Base starts at full opacity and fades to 0 as t goes 0→1
          const easeT = this._easeOutCubic(t);

          slide.style.opacity = 1 - t;
          slide.style.zIndex = 2;
          slide.style.pointerEvents = t < 0.3 ? 'auto' : 'none';

          // Exit: text moves down, product moves up (opposing directions)
          textPanel.style.transform = `translateY(${Math.round(easeT * offset)}px)`;
          textPanel.style.opacity = 1 - t;

          productPanel.style.transform = `translateY(${Math.round(-easeT * offset)}px)`;
          productPanel.style.opacity = 1 - t;

          // Product subtle scale-down on exit
          const img = slide.querySelector('.dual-scroll-story__product-image');
          if (img) img.style.transform = `scale(${1 - easeT * 0.02})`;

        } else if (i === nextIndex) {
          // NEXT SLIDE — fading in
          // Next starts at 0 opacity and fades to 1 as t goes 0→1
          const easeT = this._easeOutCubic(t);

          slide.style.opacity = t;
          slide.style.zIndex = 3;
          slide.style.pointerEvents = t > 0.7 ? 'auto' : 'none';

          // Enter: text comes from above, product comes from below (opposing)
          textPanel.style.transform = `translateY(${Math.round(-offset * (1 - easeT))}px)`;
          textPanel.style.opacity = t;

          productPanel.style.transform = `translateY(${Math.round(offset * (1 - easeT))}px)`;
          productPanel.style.opacity = t;

          // Product subtle scale-up on enter
          const img = slide.querySelector('.dual-scroll-story__product-image');
          if (img) img.style.transform = `scale(${0.98 + easeT * 0.02})`;

        } else {
          // INACTIVE — hidden
          slide.style.opacity = 0;
          slide.style.zIndex = 0;
          slide.style.pointerEvents = 'none';
          textPanel.style.transform = 'translateY(0)';
          textPanel.style.opacity = 0;
          productPanel.style.transform = 'translateY(0)';
          productPanel.style.opacity = 0;
          const img = slide.querySelector('.dual-scroll-story__product-image');
          if (img) img.style.transform = 'scale(1)';
        }
      });
    }

    /* ─── EASING FUNCTIONS ─── */

    _smoothstep(edge0, edge1, x) {
      // Hermite interpolation: stays near 0 until edge0, ramps to 1 by edge1
      const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
      return t * t * (3 - 2 * t);
    }

    _easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    /* ─── PROGRESS INDICATOR ─── */

    _createProgressIndicator() {
      if (this.slideCount <= 1 || !this._shouldSticky) return;
      if (this.querySelector('.dual-scroll-story__progress')) return; // Already created

      // Create dots container
      const dotsContainer = document.createElement('div');
      dotsContainer.classList.add('dual-scroll-story__progress');
      dotsContainer.setAttribute('aria-label', 'Slide navigation');

      for (let i = 0; i < this.slideCount; i++) {
        const dot = document.createElement('button');
        dot.classList.add('dual-scroll-story__progress-dot');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => this._scrollToSlide(i));
        dotsContainer.appendChild(dot);
      }

      this.pinWrapper.appendChild(dotsContainer);
      this.dots = Array.from(dotsContainer.querySelectorAll('.dual-scroll-story__progress-dot'));

      // Create progress bar
      const barWrapper = document.createElement('div');
      barWrapper.classList.add('dual-scroll-story__progress-bar-wrapper');
      const bar = document.createElement('div');
      bar.classList.add('dual-scroll-story__progress-bar');
      barWrapper.appendChild(bar);
      this.pinWrapper.appendChild(barWrapper);

      this.progressBar = bar;
    }

    _updateProgressIndicator(activeIndex) {
      if (!this.dots) return;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('dual-scroll-story__progress-dot--active', i === activeIndex);
      });
    }

    _updateProgressBar() {
      if (!this.progressBar) return;
      this.progressBar.style.width = (this.progress * 100) + '%';
    }

    _scrollToSlide(index) {
      if (!this._shouldSticky) return;
      const maxIndex = this.slideCount - 1;
      // Map slide index to scroll position
      const targetProgress = maxIndex > 0 ? index / maxIndex : 0;
      const targetScrollY = this.stickyStart + targetProgress * this.scrollRange;
      window.scrollTo({
        top: targetScrollY,
        behavior: 'smooth'
      });
    }

    /* ─── RESIZE ─── */

    _onResize() {
      this._calculateLayout();
    }

    /* ─── MOTION PREFERENCE ─── */

    _onMotionChange() {
      if (this._reducedMotion.matches) {
        this.classList.remove('dual-scroll-story--sticky-enabled-mobile');
        this._removeStickyHeight();
        // Show all slides normally
        this.slides.forEach(slide => {
          slide.style.opacity = 1;
          slide.style.pointerEvents = 'auto';
        });
      } else {
        this._calculateLayout();
      }
    }

    /* ─── SHOPIFY THEME EDITOR ─── */

    _onSectionLoad(e) {
      if (e.detail && e.detail.sectionId === this.sectionId) {
        this._readConfig();
        this._cacheDOM();
        if (this.slides.length > 0) {
          this._applyVerticalAlignment();
          this._calculateLayout();
          this._renderSlides(0);
        }
      }
    }

    /* ─── UTILITY ─── */

    _debounce(fn, ms) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), ms);
      };
    }
  }

  customElements.define('dual-scroll-product-story', DualScrollProductStory);
}
